import {
  Skeleton,
  Flex,
  useToast,
  CardBody,
  Card,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { analytics as commonAnalytics } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  CreditFacilityGQLType,
  TotalRiskInsightsOutputDataGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { FullDateRangePicker } from 'packages/common-frontend/components/DateRangePicker/DateRangePicker';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from '~/packages/common-frontend/components/MultiSelect';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetDailyTotalRiskInsightsQuery } from '~/packages/portfolio/requests/requests.gql.generated';
import { generateLtvChartConfig } from '~/packages/portfolio/ui/risk/portfolioRiskChartConfigs';
import { PortfolioRiskTableTemplate } from '~/packages/portfolio/ui/risk/PortfolioRiskTableTemplate';
import {
  formatRiskTableColumns,
  formatRiskTablesDataMap,
} from '~/packages/portfolio/ui/risk/PortfolioRiskTableUtils';
import { PortfolioChartCards } from '~/packages/portfolio/ui/shared/PortfolioChartCards';
import {
  Chart,
  ChartHeader,
} from '~/packages/portfolio/ui/shared/PortfolioChartTemplate';
import {
  aggregateBorrowingBaseRiskDataForSelectedCreditFacilities,
  AggregateBorrowingRiskChartData,
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  defaultSelectedTimePeriod,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
  onSelectDateRangeForOrganization,
  onToggleActiveField,
  PortfolioChartComponentType,
  PortfolioChartConfig,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '~/packages/portfolio/ui/shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '~/packages/portfolio/ui/shared/PortfolioReChart';
import { PortfolioTimePeriodSelectButtons } from '~/packages/portfolio/ui/shared/PortfolioTimePeriodSelectButtons';

// Rendering anything more than 100 chart bar items and table columns tanks page performance.
// We need to move calculations like to the backend.
const MAX_CHART_AND_COLUMN_COUNT = 100;

type PortfolioLtvChartTemplateForOrganizationProps = {
  organizationId: string;
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'slug' | 'name'>[];
};
export const PortfolioLtvChartTemplateForOrganization = ({
  organizationId,
  creditFacilities,
}: PortfolioLtvChartTemplateForOrganizationProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();

  const initialSelectedChartComponentType: PortfolioChartComponentType = 'line';
  const initialChartConfig = generateLtvChartConfig({
    chartComponentType: initialSelectedChartComponentType,
  });
  const initialActiveFields = getInitialActiveFields({
    chartConfig: initialChartConfig,
  }) as (keyof AggregateBorrowingRiskChartData)[];

  const initialSelectedCreditFacilityIds = creditFacilities.map(({ id }) => id);

  const initialCreditFacilitySelectOptions: {
    label: string;
    value: string;
  }[] = useMemo(
    () =>
      creditFacilities.map((creditFacility) => {
        return {
          value: creditFacility.id,
          label: creditFacility.name,
        };
      }),
    [creditFacilities],
  );

  const [
    selectedBar,
    setSelectedBar,
  ] = useState<AggregateBorrowingRiskChartData | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  const [
    riskInputData,
    setRiskInputData,
  ] = useState<TotalRiskInsightsOutputDataGQLType>({
    borrowingBaseRisk: [],
    receivableRisk: [],
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    AggregateBorrowingRiskChartData[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    AggregateBorrowingRiskChartData[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const [activeFields, setActiveFields] = useState<
    (keyof AggregateBorrowingRiskChartData)[]
  >(initialActiveFields);

  const [selectedCreditFacilityIds, setSelectedCreditFacilityIds] = useState<
    string[]
  >(initialSelectedCreditFacilityIds);

  const [
    selectedChartComponentType,
    setSelectedChartComponentType,
  ] = useState<PortfolioChartComponentType>(initialSelectedChartComponentType);
  const [chartConfig, setChartConfig] = useState<
    PortfolioChartConfig<keyof AggregateBorrowingRiskChartData>
  >(initialChartConfig);

  /**
   * Risk tables data state management:
   * All of the filters (cf select, date range, time period) apply to all tables
   * on the risk page
   */
  const riskTableColumns = useMemo(
    () => formatRiskTableColumns(chartDataWithTimePeriod),
    [chartDataWithTimePeriod],
  );

  const riskTablesDataMap = useMemo(
    () =>
      formatRiskTablesDataMap({
        chartDataWithDateRangeAndTimePeriod: chartDataWithTimePeriod,
        dailyBorrowingBaseRiskData: riskInputData.borrowingBaseRisk,
        dailyReceivableRiskData: riskInputData.receivableRisk,
        filteredCreditFacilities: creditFacilities.filter((facility) =>
          selectedCreditFacilityIds.includes(facility.id),
        ),
      }),
    [
      chartDataWithTimePeriod,
      creditFacilities,
      selectedCreditFacilityIds,
      riskInputData,
    ],
  );

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<AggregateBorrowingRiskChartData>(
        chartDataWithTimePeriod,
        activeFields,
      ),
    [chartDataWithTimePeriod, activeFields],
  );

  const {
    refetch: refetchRiskInsights,
    data: riskInsightsData,
    loading: isRiskInsightsLoading,
  } = useGetDailyTotalRiskInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      organizationParams: {
        organizationId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      const {
        riskData: { borrowingBaseRisk, receivableRisk },
      } = data;

      const extendedBorrowingBaseRiskData = commonAnalytics.generateExtendedData(
        borrowingBaseRisk,
        { baseDateStrKey: 'reportingDate' },
        ['fillGroupData', 'extendEnd', 'fillDate'],
        {
          extendEndDateStr: '2023-11-02',
          dataGroupKey: 'creditFacilityData',
          identifierIdKey: 'creditFacilityId',
        },
      );

      const extendedReceivableRiskData = commonAnalytics.generateExtendedData(
        receivableRisk,
        { baseDateStrKey: 'reportingDate' },
        ['fillGroupData', 'extendEnd', 'fillDate'],
        {
          extendEndDateStr: '2023-11-02',
          dataGroupKey: 'creditFacilityData',
          identifierIdKey: 'creditFacilityId',
        },
      );

      // set the daily input data for all credit facilities to be used in later filters
      // without having to make another api call for the full input set
      setRiskInputData({
        receivableRisk: extendedReceivableRiskData,
        borrowingBaseRisk: extendedBorrowingBaseRiskData,
      });

      // first filter the data and run aggregation on only the selected credit facilities
      const riskDataForSelectedFacilities = aggregateBorrowingBaseRiskDataForSelectedCreditFacilities(
        {
          selectedCreditFacilityIds,
          dailyBorrowingBaseRiskData: extendedBorrowingBaseRiskData,
        },
      );

      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate({
        chartData: riskDataForSelectedFacilities,
        selectedTimePeriod: ChartTimePeriod.day,
        maxCount: MAX_CHART_AND_COLUMN_COUNT,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate<AggregateBorrowingRiskChartData>(
          {
            chartData: riskDataForSelectedFacilities,
            selectedTimePeriod,
            maxCount: MAX_CHART_AND_COLUMN_COUNT,
          },
        );
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    },
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: creditAgreementFullArtifactsData,
    loading: isCreditAgreementFullArtifactsLoading,
  } = useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      organizationId,
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
    },
  });

  if (
    isRiskInsightsLoading ||
    !riskInsightsData ||
    !creditAgreementFullArtifactsData ||
    isCreditAgreementFullArtifactsLoading
  ) {
    return <Skeleton height="700px" />;
  }

  const {
    organization: { currentCreditAgreementArtifacts },
  } = creditAgreementFullArtifactsData;

  let parsedCreditAgreementFullArtifacts:
    | CreditAgreement.DomainEntity.CreditAgreementFullArtifact[]
    | undefined;

  try {
    parsedCreditAgreementFullArtifacts = currentCreditAgreementArtifacts.map(
      (creditAgreementFullArtifact) => {
        const { creditAgreement, id } = creditAgreementFullArtifact;
        const parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreement,
        );
        if (!parsedCreditAgreement) {
          throw Error(
            `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
          );
        }
        return parsedCreditAgreement;
      },
    );
  } catch (err) {
    const errorToastTitle = `Error parsing credit agreement artifacts for organization ${organizationId} while loading portfolio LTV charts`;
    toast({
      ...errorToastConfig,
      title: errorToastTitle,
      id: 'errorParsingCreditAgreement',
    });
    navigate('/');
  }

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate<AggregateBorrowingRiskChartData>(
      {
        chartData: chartDataForDateRange,
        selectedTimePeriod: timePeriod,
        maxCount: MAX_CHART_AND_COLUMN_COUNT,
      },
    );

    setChartDataWithTimePeriod(formattedChartData);
  };

  const onSelectCreditFacilityIds = (creditFacilityIds: string[]) => {
    setSelectedCreditFacilityIds(creditFacilityIds);

    // first filter the data and run aggregation on only the selected credit facilities
    const riskDataForSelectedFacilities = aggregateBorrowingBaseRiskDataForSelectedCreditFacilities(
      {
        selectedCreditFacilityIds: creditFacilityIds,
        dailyBorrowingBaseRiskData: riskInputData.borrowingBaseRisk,
      },
    );

    // set base chart data for date range with the newly aggregated data
    const formattedChartData = formatChartDataByReportingDate({
      chartData: riskDataForSelectedFacilities,
      selectedTimePeriod: ChartTimePeriod.day,
      maxCount: MAX_CHART_AND_COLUMN_COUNT,
    });
    setChartDataForDateRange(formattedChartData);

    // then set data for the specific time period
    if (selectedTimePeriod !== ChartTimePeriod.day) {
      const formattedChartDataForPeriod = formatChartDataByReportingDate<AggregateBorrowingRiskChartData>(
        {
          chartData: riskDataForSelectedFacilities,
          selectedTimePeriod,
          maxCount: MAX_CHART_AND_COLUMN_COUNT,
        },
      );
      setChartDataWithTimePeriod(formattedChartDataForPeriod);
    } else {
      setChartDataWithTimePeriod(formattedChartData);
    }
  };

  const onSelectChartComponentType = (
    chartComponentType: PortfolioChartComponentType,
  ) => {
    setSelectedChartComponentType(chartComponentType);

    const newChartConfig = generateLtvChartConfig({ chartComponentType });
    setChartConfig(newChartConfig);
  };

  return (
    <Card variant="outline" size="lg" mb="40px">
      <CardBody display="flex" flexDirection="column" gap="1.5rem">
        <Chart
          header={
            <Flex direction="column" gap="1rem">
              <ChartHeader
                right={
                  <>
                    <IsolatedMultiSelect
                      applyButtonText="Apply filters"
                      buttonProps={{
                        width: ['100%', '300px'],
                      }}
                      counterLabel="Credit facilities"
                      initialSelections={selectedCreditFacilityIds}
                      isSearchable
                      menuListProps={{ minWidth: '30rem' }}
                      onApplyOptions={onSelectCreditFacilityIds}
                      options={initialCreditFacilitySelectOptions}
                      optionsHeader="Credit facilities"
                      placeholderText="Select credit facilities"
                      showSelectedCount
                      titleText="All credit facilities"
                    />
                    <FullDateRangePicker
                      currentDateRange={{
                        startDate: chartDataDateRange.chartDataStartDate,
                        endDate: chartDataDateRange.chartDataStartDate,
                      }}
                      onSelectDateRange={(newDateRange) =>
                        onSelectDateRangeForOrganization({
                          newDateRange,
                          refetch: refetchRiskInsights,
                          organizationId,
                          setChartDataDateRange,
                        })
                      }
                      width={['100%', '300px']}
                    />
                    <PortfolioTimePeriodSelectButtons
                      currentSelectedTimePeriod={selectedTimePeriod}
                      onSelectTimePeriod={onSelectTimePeriod}
                    />
                  </>
                }
                left={
                  <>
                    <Flex gap="0.5rem">
                      <IconButtonV2
                        aria-label="Line Chart"
                        placement="bottom"
                        variant="primary-muted"
                        colorScheme="gray"
                        icon={<Icon icon="chart-line" />}
                        onClick={() => onSelectChartComponentType('line')}
                        isActive={selectedChartComponentType === 'line'}
                      />
                      <IconButtonV2
                        aria-label="Bar Chart"
                        placement="bottom"
                        variant="primary-muted"
                        colorScheme="gray"
                        icon={<Icon icon="chart-column" />}
                        onClick={() => onSelectChartComponentType('bar')}
                        isActive={selectedChartComponentType === 'bar'}
                      />
                    </Flex>
                    <PortfolioDownloadCsvLink
                      dataRows={dataRows}
                      headers={headers}
                      chartTitle="LTV"
                      selectedTimePeriod={selectedTimePeriod}
                      MenuButtonProps={{
                        mt: 0,
                        as: IconButtonV2,
                        'aria-label': 'Download',
                        icon: <Icon icon="arrow-down-to-bracket" />,
                        variant: 'primary-muted',
                        placement: 'bottom',
                      }}
                    />
                  </>
                }
              />
              {chartDataWithTimePeriod.length >= MAX_CHART_AND_COLUMN_COUNT && (
                <Alert status="warning">
                  <AlertIcon />
                  <Flex direction="row" flexWrap="wrap">
                    <AlertTitle>Large dataset.</AlertTitle>
                    <AlertDescription>
                      To improve performance, only the latest{' '}
                      {MAX_CHART_AND_COLUMN_COUNT} data points are shown.
                    </AlertDescription>
                  </Flex>
                </Alert>
              )}
            </Flex>
          }
          chart={
            <PortfolioReChart
              chartData={chartDataWithTimePeriod}
              selectedTimePeriod={selectedTimePeriod}
              currentSelectedBarReportingDate={
                selectedBar
                  ? selectedBar.reportingDate
                  : getDefaultDashboardDisplayEndDate()
              }
              onSelectBar={setSelectedBar}
              chartConfig={chartConfig}
              visibleChartComponents={activeFields}
              // lowest y axis domain is 0% for LTV
              leftYAxisDomain={[0, 'auto']}
            />
          }
          sidebar={
            selectedBar && (
              <PortfolioChartCards
                selectedBarData={selectedBar}
                chartConfig={chartConfig}
                activeFields={activeFields}
                onToggleActiveField={(selectedField) =>
                  onToggleActiveField({
                    selectedField,
                    currentActiveFields: activeFields,
                    setActiveFields,
                  })
                }
              />
            )
          }
        />

        <PortfolioRiskTableTemplate
          portfolioRiskTableRows={riskTablesDataMap.collateralCovenantLtv}
          columns={riskTableColumns}
          maxColumnCount={MAX_CHART_AND_COLUMN_COUNT}
          tableTitle="Collateral Covenant LTV"
        />
        <PortfolioRiskTableTemplate
          portfolioRiskTableRows={riskTablesDataMap.netOutstandingLtv}
          columns={riskTableColumns}
          maxColumnCount={MAX_CHART_AND_COLUMN_COUNT}
          tableTitle="Net Outstanding LTV"
        />
        <PortfolioRiskTableTemplate
          portfolioRiskTableRows={riskTablesDataMap.performingLtv}
          columns={riskTableColumns}
          maxColumnCount={MAX_CHART_AND_COLUMN_COUNT}
          tableTitle="Performing LTV"
        />
        <PortfolioRiskTableTemplate
          portfolioRiskTableRows={
            riskTablesDataMap.chargedOffPercentageOfTotalExposure
          }
          columns={riskTableColumns}
          maxColumnCount={MAX_CHART_AND_COLUMN_COUNT}
          tableTitle="Charge Offs"
          creditAgreementArtifacts={parsedCreditAgreementFullArtifacts}
          creditFacilities={creditFacilities}
        />
        <PortfolioRiskTableTemplate
          portfolioRiskTableRows={
            riskTablesDataMap.delinquentPercentageOfTotalExposure
          }
          columns={riskTableColumns}
          maxColumnCount={MAX_CHART_AND_COLUMN_COUNT}
          tableTitle="Delinquencies"
          creditAgreementArtifacts={parsedCreditAgreementFullArtifacts}
          creditFacilities={creditFacilities}
        />
      </CardBody>
    </Card>
  );
};
